import * as React from 'react';
import * as Images from 'pos/Images';
import { Res, TicketLineDTO, TicketLineStatus, SoldProductType } from 'core/api';
import { ActionButtonDefinition, PosScreen, SaleHeader, Footer, HQuickActionsPanel, TicketControl, Content } from 'pos/ui';
import * as SalesStore from '../SalesStore';
import { Command } from 'pos/common';
import { Message, Modal } from 'core/ui-next/modal';
import { PhoneHeaderContent } from '../sale/HeaderContent';
import { SaleScreenProps } from '../sale/SaleScreenProps';
import * as CoreImages from 'core/img';
import { TicketTotal } from '../sale/TicketTotal';
import { Box, Flex } from '@chakra-ui/react';
import { getLastSelectableLine } from '../sale/useSaleScreenEvents';

function editTicketLineButton(line: Nullable<TicketLineDTO>, refresh: () => void): ActionButtonDefinition {
	return {
		title: Res.Web.EDIT_TICKET_LINE(),
		image: Images.EditTicketLine,
		action: async () => {
			if (line) {
				await Command.execute('editTicketLine', line, {
					showAddNotesOrEditMenu: false,
					showChangeAddins: false,
					showAdjustIngredients: true,
					showRemoveLine: false,
					showSetPreparationOrder: false,
					showSetTicketLineCreator: false,
					showPrepare: true,
					showRequest: true,
					showProductDetails: true,
					showSetDiscounts: true,
					showSetPrice: true,
					showSetQuantity: true,
					showEditSeatNumber: line.Type !== SoldProductType.MenuItem
				});
				refresh();
			}
		}
	};
}

function setTicketLinePreparationOrderButton(line: Nullable<TicketLineDTO>, refresh: () => void): ActionButtonDefinition {
	return {
		title: Res.Web.SET_LINE_PREPARATION_ORDER_CURRENT_ORDER_LBL(),
		image: Images.SetTicketLinePreparationOrder,
		action: async () => {
			if (line) {
				await Command.execute('setLinePreparationOrder', line);
				refresh();
			}
		}
	};
}

async function addTicketLineNotOrEditMenu(line: TicketLineDTO) {
	if (line.Type === SoldProductType.MenuHeader) {
		await Command.execute('composeMenu', line, 'edition');
	}
	else if (line.Status !== TicketLineStatus.New) {
		await Message.show(Res.Msg.MSG_LINE_HAS_ALREADY_BEEN_ORDERED());
	}
	else {
		await Command.execute('addTicketLineNote', line);
	}
}

function addTicketLineNoteOrEditMenuButton(line: Nullable<TicketLineDTO>, refresh: () => void): ActionButtonDefinition {
	return {
		title: line && line.Type === SoldProductType.MenuHeader ? Res.Web.COMPOSE_MENU() : Res.Web.ADD_TICKET_LINE_NOTE(),
		image: line && line.Type === SoldProductType.MenuHeader ? Images.EditMenu : Images.GetNotes,
		action: async () => {
			if (line) {
				await addTicketLineNotOrEditMenu(line);
				refresh();
			}
		}
	};
}

function composeProductButton(line: Nullable<TicketLineDTO>, refresh: () => void): ActionButtonDefinition {
	return {
		title: Res.Web.COMPOSE_PRODUCT(),
		image: Images.ComposeProduct,
		action: async () => {
			if (line) {
				await Command.execute('setTicketLineAddins', line);
				refresh();
			}
		}
	};
}

function cancelTicketLineButton(line: Nullable<TicketLineDTO>, refresh: () => void): ActionButtonDefinition {
	return {
		title: line && line.Status !== TicketLineStatus.New && !line.HasBeenRequested ? Res.Web.CANCEL_TICKET_LINE_BTN() : Res.Web.DELETE_TICKET_LINE_BTN(),
		image: line && line.Status !== TicketLineStatus.New && !line.HasBeenRequested ? Images.CancelTicketLine : Images.DeleteTicketLine,
		action: async () => {
			if (line) {
				await Command.execute('removeTicketLine', line);
				refresh();
			}
		}
	};
}

export default function ViewTicket(props: SaleScreenProps) {
	const [ticket, setTicket] = React.useState(() => SalesStore.getTicket());
	const [selectedLine, setSelectedLine] = React.useState<Nullable<TicketLineDTO>>(
		ticket?.Lines?.length ? ticket.Lines[ticket.Lines.length - 1] : null
	);
	const { cancel } = props;

	const refreshTicket = React.useCallback(() => {
		const updatedTicket = SalesStore.getTicket();
		setTicket(updatedTicket);
		setSelectedLine(getLastSelectableLine(updatedTicket, selectedLine));
	}, [selectedLine]);

	const selectLine = React.useCallback((line: Nullable<TicketLineDTO>) => {
		if (line)
			setSelectedLine(line);
	}, [setSelectedLine]);

	const onBack = React.useCallback(async () => {
		cancel();
	}, [cancel]);

	return (
		<PosScreen screenId='VIEW_TICKET' size={'full-screen'}>
			<SaleHeader content={<PhoneHeaderContent ticket={ticket} seatNumber={SalesStore.getSeatNumber()} preparationOrder={SalesStore.getPreparationOrder()} isShowTicketButton={false} onBack={onBack} />} />

			<Content>
				<Flex bg={{ base: '{colors.neutral.100}', _dark: '{colors.neutral.900}' }} w={'full'} height={'full'} direction="column" flex="1" position="relative" paddingX={'2'} paddingY={'1'} gap={0}>
					<Box flex="1" minH={0} overflow="auto">
						<TicketControl ticket={ticket} selectedLine={selectedLine} onSelect={selectLine} />
					</Box>
					<Box flexShrink={0}>
						<TicketTotal ticket={ticket} />
					</Box>
				</Flex>
			</Content>

			<Footer>
				<HQuickActionsPanel
					buttons={[
						{ title: Res.Common.BACK_BTN(), image: CoreImages.Back, action: onBack },
						cancelTicketLineButton(selectedLine, refreshTicket),
						composeProductButton(selectedLine, refreshTicket),
						addTicketLineNoteOrEditMenuButton(selectedLine, refreshTicket),
						setTicketLinePreparationOrderButton(selectedLine, refreshTicket),
						editTicketLineButton(selectedLine, refreshTicket)
					]}
					surface='footer'
				/>
			</Footer>
		</PosScreen>
	);
}

declare global {
	interface CommandType {
		showCurrentTicket: () => Promise<Result>
	}
}

Command.register('showCurrentTicket', 'NO_PRECONDITION', async () => await Modal.show(ViewTicket));


